import React from "react";
import AppHeading, { Action } from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import DataTable from "../components/data-table/data-table";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import GenericAddDialog from "./components/dialogs/generic-add-dialog";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../providers/auth-provider";
import isAdmin from "../functions/is-admin";
import { v4 as uuid } from "uuid";
import axios, { errorHandler, responseHandler } from "../requests/axios";
import { toast } from "react-toastify";
import GenericEditDialog from "./components/dialogs/generic-edit-dialog";
import AttachChannelToOrgDialog from "./components/dialogs/attach-channel-to-org-dialog";
import ManagePermissionsDialog from "./components/dialogs/manage-permissions-dialog";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";

export default function OrganisationPage() {
  const [organisation, setOrganisation] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [showAddUserDialog, setShowUserDialog] = React.useState(false);
  const [editUserDialog, setEditUserDialog] = React.useState({
    open: false,
    user: null,
  });
  const [showAttachChannelDialog, setAttachChannelDialog] =
    React.useState(false);
  const [permissionsDialog, setPermissionsDialog] = React.useState({
    open: false,
    user: null,
  });
  const userTableRef = React.useRef();
  const channelTableRef = React.useRef();
  const navigate = useNavigate();
  const { organisation_id } = useParams();
  const { user } = useAuth();

  const detachChannel = React.useCallback(
    (channelId) => {
      axios
        .post(
          `api/organisations/${organisation_id}/channels/${channelId}/detach`
        )
        .then(
          responseHandler(() => {
            toast.success("Channel unlinked successfully");
            refreshChannelsTable();
          })
        )
        .catch(
          errorHandler(() => {
            toast.error("Something went wrong while contacting the server");
          })
        );
    },
    [organisation_id]
  );

  const userColumns = React.useMemo(
    () => [
      {
        label: "Name",
        key: "name",
        value: "name",
        width: 40,
      },
      {
        label: "Email",
        key: "email",
        value: "email",
        width: 30,
      },
      {
        label: "",
        value: "",
        key: "0",
        width: 30,
        renderValue: (row) => {
          return (
            <span className="data-table__action-cell">
              <span
                className="data-table__action-cell__item data-table__icon"
                onClick={() => openUserEditDialog(row)}
              >
                <EditRoundedIcon />
              </span>
              <span
                className="data-table__action-cell__item data-table__icon"
                onClick={() => openPermissionsDialog(row)}
              >
                <ShieldRoundedIcon />
              </span>
            </span>
          );
        },
      },
    ],
    []
  );

  const channelColumns = React.useMemo(
    () => [
      {
        label: "Display Name",
        key: "channel_display_name",
        value: "channel_display_name",
        width: 30,
      },
      {
        label: "Name",
        key: "channel_name",
        value: "channel_name",
        width: 30,
      },
      {
        label: "Parent",
        key: "parent_channel_name",
        value: "parent_channel_name",
        width: 30,
      },
      {
        label: "",
        value: "",
        key: "0",
        width: 10,
        renderValue: (row) => {
          return (
            <span className="data-table__action-cell">
              <span
                className="data-table__action-cell__item data-table__icon"
                onClick={() => detachChannel(row.channel_id)}
              >
                <LinkOffRoundedIcon />
              </span>
            </span>
          );
        },
      },
    ],
    [detachChannel]
  );

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`api/organisations/${organisation_id}`)
      .then(
        responseHandler((response) => {
          setOrganisation(response.data);
        })
      )
      .catch(
        errorHandler(() => {
          toast.error("Error occurred while loading Organisation");
        })
      )
      .finally(() => setLoading(false));
  }, [organisation_id]);

  // admin-only page
  if (!isAdmin(user)) {
    navigate("/");
    return;
  }

  function openNewUserDialog() {
    setShowUserDialog(true);
  }

  function closeNewUserDialog() {
    setShowUserDialog(false);
  }

  function openUserEditDialog(user) {
    setEditUserDialog({ open: true, user });
  }

  function closeUserEditDialog() {
    setEditUserDialog({ open: false, user: null });
  }

  function openAttachChannelDialog() {
    setAttachChannelDialog(true);
  }

  function closeAttachChannelDialog() {
    setAttachChannelDialog(false);
  }

  function openPermissionsDialog(user) {
    setPermissionsDialog({ open: true, user });
  }

  function closePermissionsDialog() {
    setPermissionsDialog({ open: false, user: null });
  }

  function refreshUsersTable() {
    if (userTableRef.current) {
      userTableRef.current.refreshList();
    }
  }

  function refreshChannelsTable() {
    if (channelTableRef.current) {
      channelTableRef.current.refreshList();
    }
  }

  function createUser(payload) {
    return axios.post(`api/organisations/${organisation_id}/users`, payload);
  }

  function updateUser(payload) {
    return axios.put(
      `api/organisations/${organisation_id}/users/${payload.id}`,
      payload
    );
  }

  return (
    <React.Fragment>
      <AppHeading
        title={
          loading || !organisation
            ? "Loading..."
            : organisation.organisation_name
        }
        subtitle={
          loading || !organisation
            ? ""
            : "Add, Remove or Edit Users or Link or Unlink Channels from the Organisation here"
        }
      />

      <AppBody loading={loading}>
        <div className="organisation-page">
          <div className="organisation-page__table-header">Users</div>
          <div className="organisation-page__action-row">
            <Action
              action={{
                onClick: openNewUserDialog,
                balloonLabel: "Add user",
                icon: AddCircleRoundedIcon,
              }}
            />
          </div>
          <DataTable
            url={`/api/organisations/${organisation_id}/users`}
            columns={userColumns}
            ref={userTableRef}
          />

          <div className="organisation-page__divider" />

          <div className="organisation-page__table-header">Channels</div>
          <div className="organisation-page__action-row">
            <Action
              action={{
                onClick: openAttachChannelDialog,
                balloonLabel: "Attach Channels",
                icon: AddCircleRoundedIcon,
              }}
            />
          </div>
          <DataTable
            url={`api/organisations/${organisation_id}/channels`}
            columns={channelColumns}
            ref={channelTableRef}
          />
        </div>
      </AppBody>
      <GenericAddDialog
        onSubmit={createUser}
        onSuccess={() => refreshUsersTable()}
        onClose={closeNewUserDialog}
        isOpen={showAddUserDialog}
        title="Add Organisation User"
        successMessage="User added successfully"
        errorMessage="Error occurred while adding User"
        fields={[
          {
            type: "text",
            label: "Name",
            name: "name",
            required: true,
          },

          {
            type: "text",
            label: "Username",
            name: "username",
            required: true,
          },
          {
            type: "text",
            label: "Email",
            name: "email",
            required: true,
          },
          {
            type: "password",
            label: "Password",
            name: "password",
            required: true,
          },
        ]}
        key={`add-dialog-${uuid()}`}
      />
      <GenericEditDialog
        data={editUserDialog.user}
        onSubmit={updateUser}
        onSuccess={() => refreshUsersTable()}
        onClose={() => closeUserEditDialog()}
        isOpen={editUserDialog.open}
        title="Edit Organisation User"
        successMessage="User saved successfully"
        errorMessage="Error occurred while updating User"
        fields={[
          {
            type: "text",
            label: "Name",
            name: "name",
            required: true,
          },

          {
            type: "text",
            label: "Username",
            name: "username",
            required: true,
          },
          {
            type: "text",
            label: "Email",
            name: "email",
            required: true,
          },
          {
            type: "password",
            label: "Password",
            name: "password",
            required: false, // not required for Edit
          },
        ]}
        key={`edit-dialog-${uuid()}`}
      />
      <AttachChannelToOrgDialog
        onClose={closeAttachChannelDialog}
        onConfirm={() => refreshChannelsTable()}
        isOpen={showAttachChannelDialog}
        organisationId={organisation_id}
        key={`attach-channel-dialog-${uuid()}`}
      />
      <ManagePermissionsDialog
        isOpen={permissionsDialog.open}
        user={permissionsDialog.user}
        onClose={closePermissionsDialog}
        onConfirm={refreshUsersTable}
      />
    </React.Fragment>
  );
}
